import { useTranslation } from 'react-i18next';

/**
 * Hook to format a date string into a "time ago" string with internationalization.
 *
 * @returns {Object} An object containing the timeAgo function.
 */
export function useTimeAgo() {
  const { t } = useTranslation('common');

  /**
   * Formats a date into a "time ago" string.
   *
   * @param date - ISO string or Date instance.
   * @returns The formatted "time ago" string.
   */
  const timeAgo = (date: string | Date): string => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60_000);

    if (mins < 60) {
      return t('timeAgo.minutes', {
        count: Math.max(0, mins),
        defaultValue: '{{count}}m ago',
      });
    }

    const hours = Math.floor(mins / 60);

    if (hours < 24) {
      return t('timeAgo.hours', {
        count: hours,
        defaultValue: '{{count}}h ago',
      });
    }

    return t('timeAgo.days', {
      count: Math.floor(hours / 24),
      defaultValue: '{{count}}d ago',
    });
  };

  return { timeAgo };
}
